import { FC, useMemo } from 'react';
import { useFlightRooms, useSettings, useTimeZone } from '@connect-x-app/hybrid-state';
import { BadgePill, Text } from '@connect-x-app/hybrid-ui';
import { TimeZone } from '@connect-x-app/shared-domain-models';
import { COLORS, LAYERS } from '@connect-x-app/shared-ui';
import { getFlightroomAirportNames, isRoomLanded } from '@connect-x-app/shared-utils';
import { CHANNEL_TEST_IDS } from '@connect-x-app/web-test-ids';
import { Divider, Icon } from '@connect-x-app/web-ui';

import { CountdownTimer } from '../countdownTimer/CountdownTimer';
import { GrantAccess } from './components';

import './ChatHeader.scss';

type Props = {
  isPreviewMode?: boolean;
  onClickInfo: () => void;
};

export const ChatHeader: FC<Props> = ({ isPreviewMode = false, onClickInfo }) => {
  const { selectedFlightRoom } = useFlightRooms();
  const { arrivalAirport, departureAirport } = getFlightroomAirportNames(selectedFlightRoom);
  const { settings } = useSettings();
  const { formatLongDateInIanaTimeZone, timeZoneAbbreviation } = useTimeZone();

  const timeZoneText = useMemo(
    () =>
      settings.preferences.timeZone === TimeZone.UTC
        ? settings.preferences.timeZone
        : `${settings.preferences.timeZone} (${timeZoneAbbreviation})`,
    [settings.preferences.timeZone, timeZoneAbbreviation],
  );

  return (
    <header className="chat-header-container">
      <section className="chat-header-middle">
        <div>
          <Text as="span" className="chat-header-middle--flight-number" color={COLORS.zulu.$08} size="SM" weight="bold">
            {selectedFlightRoom?.flightNumber}
          </Text>
          <Text as="span" color={COLORS.zulu.$08} size="SM">
            {selectedFlightRoom
              ? `- ${formatLongDateInIanaTimeZone(selectedFlightRoom.schedule.atd ?? selectedFlightRoom.schedule.etd ?? selectedFlightRoom.schedule.std, selectedFlightRoom.departure.timeZone ?? '')}`
              : ''}
          </Text>
        </div>
        <Divider />
        <div className="chat-header-middle--flight-airports" data-test={CHANNEL_TEST_IDS.header.locations}>
          <Text as="span" color={COLORS.zulu.$08} size="SM" weight="bold">
            {departureAirport}
          </Text>
          <Icon colorName={COLORS.zulu.$08} name="aircraftPlaneRightFilled" size={2} />
          <Text as="span" color={COLORS.zulu.$08} size="SM" weight="bold">
            {arrivalAirport}
          </Text>
        </div>
        {selectedFlightRoom && !isRoomLanded(selectedFlightRoom) ? (
          <>
            <Divider />
            <CountdownTimer flightroom={selectedFlightRoom} />
          </>
        ) : null}
      </section>
      <section className="chat-header-right">
        {!isPreviewMode ? (
          <>
            <BadgePill
              leftIcon={
                <Icon colorName={LAYERS.iconColors['$icon-primary']} name="datetimeClockgenericTemplateOutline" size={1.6} />
              }
              size="xs"
              testId={CHANNEL_TEST_IDS.header.timeZone}
              text={timeZoneText}
              type="primary-white"
            />
            <GrantAccess />
            <Icon
              colorName={LAYERS.iconColors['$icon-primary']}
              hoverColorName={COLORS.romeo.$07}
              name="circularInfoOutline"
              onClick={onClickInfo}
              size={2.4}
              testId={CHANNEL_TEST_IDS.header.info}
            />
          </>
        ) : null}
      </section>
    </header>
  );
};
